import { AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { SearchInput } from '@/components/ui/search-input';
import type { EmailItem } from '@/config/office/types';

import { InboxEmailsList } from './InboxEmailsList';
import { SelectedEmailsList } from './SelectedEmailsList';

interface CustomFilterContentProps {
  counterHighlight: boolean;
  debouncedSearch: string;
  emails: Array<EmailItem>;
  error: Error | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onSearchChange: (query: string) => void;
  onToggleEmail: (id: string) => void;
  resultCount: number;
  searchQuery: string;
  selectedCount: number;
  selectedEmailIds: Set<string>;
  selectedEmailItems: Array<EmailItem>;
  selectedEmailsSectionRef: React.RefObject<HTMLDivElement | null>;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  showCheckboxes: boolean;
}

export function CustomFilterContent({
  counterHighlight,
  debouncedSearch,
  emails,
  error,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onSearchChange,
  onToggleEmail,
  resultCount,
  searchQuery,
  selectedCount,
  selectedEmailIds,
  selectedEmailItems,
  selectedEmailsSectionRef,
  sentinelRef,
  showCheckboxes,
}: CustomFilterContentProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className="px-3 py-2">
        <SearchInput
          onChange={onSearchChange}
          placeholder={t('assistant.emailContext.searchPlaceholder')}
          value={searchQuery}
        />
      </div>

      {/* Email List — scroll container is the parent inboxScrollRef in EmailContextDialog */}
      <div>
        <AnimatePresence initial={false}>
          {selectedEmailItems.length > 0 && (
            <SelectedEmailsList
              emails={selectedEmailItems}
              key="selected-emails"
              onToggleEmail={onToggleEmail}
              sectionRef={selectedEmailsSectionRef}
              showCheckboxes={showCheckboxes}
            />
          )}
        </AnimatePresence>

        <InboxEmailsList
          debouncedSearch={debouncedSearch}
          emails={emails}
          error={error}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          onToggleEmail={onToggleEmail}
          selectedEmailIds={selectedEmailIds}
          sentinelRef={sentinelRef}
          showCheckboxes={showCheckboxes}
        />
      </div>

      {/* Footer */}
      <div className="bg-back-bg flex items-center justify-between px-3 py-2.5">
        {debouncedSearch.trim() && emails.length > 0 && (
          <span className="text-tertiary text-xs font-medium">
            {t('assistant.emailContext.results', { count: resultCount })}
          </span>
        )}
        <span
          className={`text-tertiary ml-auto text-xs font-medium transition-all duration-300 ${
            counterHighlight ? 'text-primary scale-110 font-semibold' : 'scale-100'
          }`}
        >
          {t('assistant.emailContext.selectedCount', { count: selectedCount })}
        </span>
      </div>
    </>
  );
}
